import React, { useState } from 'react';
import { Calendar, CircleCheck, CircleX, PauseCircle, Gavel, ChevronDown, ChevronUp } from 'lucide-react';

const getOutcomeStyles = (outcome, isDarkMode) => {
  const normalized = (outcome || '').toLowerCase();
  if (normalized === 'passed' || normalized === 'approved') {
    return {
      className: isDarkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
      icon: CircleCheck
    };
  }
  if (normalized === 'failed' || normalized === 'denied' || normalized === 'rejected') {
    return {
      className: isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700',
      icon: CircleX
    };
  }
  if (normalized === 'tabled' || normalized === 'deferred' || normalized === 'postponed') {
    return {
      className: isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800',
      icon: PauseCircle
    };
  }
  return {
    className: isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700',
    icon: Gavel
  };
};

const formatDate = (date) => {
  if (!date) return 'Date unavailable';
  // Parse date as local date to avoid timezone issues
  let dateObj;
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    // Parse YYYY-MM-DD format as local date (not UTC)
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    dateObj = new Date(year, month - 1, day);
  } else {
    dateObj = new Date(date);
  }
  if (isNaN(dateObj.getTime())) return 'Date unavailable';
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const VerdictCard = ({ verdict, isDarkMode }) => {
  const outcomeStyles = getOutcomeStyles(verdict.outcome, isDarkMode);
  const OutcomeIcon = outcomeStyles.icon;
  const [isExpanded, setIsExpanded] = useState(false);
  const voteCounts = verdict.voteCounts || {};

  const outcomeLabel = verdict.outcome ? verdict.outcome.toUpperCase() : 'UNKNOWN';

  return (
    <div
      className={`group relative bg-transparent rounded-xl hover:shadow-lg transition-all duration-300 border overflow-hidden ${isDarkMode ? 'border-gray-700 hover:border-blue-600' : 'border-gray-200 hover:border-blue-200'}`}
      style={{ padding: '1.5rem 2rem' }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${outcomeStyles.className}`}>
          <OutcomeIcon className="w-3 h-3 mr-1" />
          {outcomeLabel}
        </span>
        <span className={`flex items-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Calendar className="w-4 h-4 mr-1" />
          {formatDate(verdict.meetingDate || verdict.createdAt)}
        </span>
      </div>

      <h3 className={`text-lg md:text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        {verdict.title}
      </h3>
      <p className={`text-sm md:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {verdict.description || 'No description available.'}
      </p>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className={`inline-flex items-center text-sm font-semibold transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        >
          {isExpanded ? 'Hide vote counts' : 'View vote counts'}
          {isExpanded ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
        </button>

        {isExpanded && (
          <div className={`mt-3 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-800/60 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex flex-col">
                <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Yes</span>
                <span className="text-lg font-semibold">{voteCounts.yes ?? 0}</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No</span>
                <span className="text-lg font-semibold">{voteCounts.no ?? 0}</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Absent</span>
                <span className="text-lg font-semibold">{voteCounts.absent ?? 0}</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Abstain</span>
                <span className="text-lg font-semibold">{voteCounts.abstain ?? 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerdictCard;
